import React, { useState } from "react";
import { InputGroup, Form } from "react-bootstrap";
import InvoiceAppointmentModal from "./AppointmentPicker"; // Adjust path as needed
import { isLineDateOnly } from "../../utils/appointmentTimes";

export default function AppointmentDateInput({ 
  patientId, 
  fetchAppointments, 
  onAppointmentSelect,
  initial,
  error = "",
  value = null // Current appointment data
}) {
  const [showModal, setShowModal] = useState(false);

  // Helper function to format datetime string
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      const dt = new Date(dateTimeStr);
      return dt.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}:\d{2})/, '$3-$1-$2 $4');
    } catch (e) {
      return dateTimeStr; // Fallback to original string
    }
  };

  // Format display text based on current value structure
  const getDisplayText = () => {
    if (!value) return "";

    // Date-only lines: show just the date(s), no times.
    const dateOnly = isLineDateOnly(value);

    if (value.useCustomDate) {
      // Custom date path - check for both formats
      if (value.appointmentCustomStart) {
        if (dateOnly) {
          const startDate = value.appointmentCustomStart.slice(0, 10);
          const endDate = value.appointmentCustomEnd
            ? value.appointmentCustomEnd.slice(0, 10)
            : "";
          return endDate && endDate !== startDate
            ? `${startDate} - ${endDate}`
            : startDate;
        }
        const startText = formatDateTime(value.appointmentCustomStart);
        const endText = value.appointmentCustomEnd ?
          ` - ${formatDateTime(value.appointmentCustomEnd)}` : "";
        return `${startText}${endText}`;
      }
      // Fallback to date/time format
      if (value.startDate && value.startTime) {
        const endText = value.endDate && value.endTime
          ? ` - ${value.endDate} ${value.endTime}`
          : "";
        return `${value.startDate} ${value.startTime}${endText}`;
      }
    } else {
      // Linked appointment path - check for both formats
      if (value.appointmentStartSet) {
        if (dateOnly) {
          const startDate = value.appointmentStartSet.slice(0, 10);
          const endDate = value.appointmentEndSet
            ? value.appointmentEndSet.slice(0, 10)
            : "";
          return endDate && endDate !== startDate
            ? `${startDate} - ${endDate}`
            : startDate;
        }
        const startText = formatDateTime(value.appointmentStartSet);
        const endText = value.appointmentEndSet ?
          ` - ${formatDateTime(value.appointmentEndSet)}` : "";
        return `${startText}${endText}`;
      }
      // Fallback to date/time format
      if (value.startDate && value.startTime) {
        const endText = value.endDate && value.endTime
          ? ` - ${value.endDate} ${value.endTime}`
          : "";
        return `${value.startDate} ${value.startTime}${endText}`;
      }
      // Final fallback to appointmentDateSet if no time info
      if (value.appointmentDateSet) {
        return value.appointmentDateSet;
      }
    }

    return "";
  };

  const handleModalConfirm = (appointmentData) => {
    onAppointmentSelect?.(appointmentData);
    setShowModal(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onAppointmentSelect?.(null);
  };

  const displayText = getDisplayText();

  return (
    <>
      <InputGroup 
        style={{ cursor: 'pointer' }} 
        onClick={() => setShowModal(true)}
        className={error ? 'border-danger' : ''}
      >
        <InputGroup.Text 
          style={{ 
            backgroundColor: error ? '#f8d7da' : 'rgb(206.6, 226, 254.6)',
            border: `1px solid ${error ? '#dc3545' : 'rgb(206.6, 226, 254.6)'}`,
            borderRight: 'none',
            cursor: 'pointer'
          }}
        >
          <i className="fa-regular fa-calendar-days text-primary"></i>
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Click to select appointment date & time"
          value={displayText}
          readOnly
          style={{
            borderLeft: 'none',
            height: '30px',
            cursor: 'pointer',
            backgroundColor: 'white',
            borderColor: error ? '#dc3545' : '#ced4da'
          }}
          aria-invalid={!!error}
          isInvalid={!!error}
        />
        {displayText && (
          <InputGroup.Text 
            onClick={handleClear}
            style={{ 
              backgroundColor: '#f8f9fa',
              border: `1px solid ${error ? '#dc3545' : '#ced4da'}`,
              borderLeft: 'none',
              cursor: 'pointer'
            }}
          >
            <i className="fa-solid fa-times text-muted"></i>
          </InputGroup.Text>
        )}
      </InputGroup>

      {value?.isInvoiced && (
        <div className="text-warning small mt-1">
          <i className="fa-solid fa-triangle-exclamation me-1"></i>
          This appointment was already invoiced before.
        </div>
      )}

      <InvoiceAppointmentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        patientId={patientId}
        fetchAppointments={fetchAppointments}
        initial={initial}
        onConfirm={handleModalConfirm}
        initialValue={value} // Pass current value to modal for editing
      />
    </>
  );
}